/**
 * The merged sidebar entries and their in-page tab strips (#2476, epic #2469
 * C.1) — the single source for both.
 *
 * A merged entry is ONE sidebar row that owns several sibling routes. Each
 * route keeps its own page; the pages share a tab strip (`NavGroupTabs`), so
 * no route is added or removed. Three things are derived from this table and
 * from nothing else:
 *
 *   - the strip itself (which tabs, which labels, which permission gates);
 *   - which sidebar row lights on a URL (`navNamespacesOf` →
 *     `isNavItemActive` in `sidebar-config.tsx`);
 *   - the top bar's breadcrumb, which reads the lit row.
 *
 * A tab shows only when the seat holds EVERY key in its `permissions` — the
 * same keys its route's `<PermissionGuard>` checks in `App.tsx`. Omitted means
 * the route has no permission gate of its own (the audit and webhook routes are
 * gated by the admin ROLE, at `<AdminGuard>` and the 管理 group's `adminOnly`).
 *
 * The first tab is the entry's landing tab: the bare `entry` URL redirects to
 * it in `App.tsx` (`/audit` → operations, `/webhooks` → health).
 *
 * Each tab is written as a `labelKey: '…'` literal, and that shape is
 * load-bearing: `architecture/i18n-data-driven-keys` scans for exactly this form
 * and uses `auditWebhooks.tab.events` as one of its sentinels. Building the labels
 * any other way compiles and renders the same while quietly removing them from
 * the lock that proves both locales carry them.
 */
import { PERMISSIONS, type PermissionKey } from '@/permissions';
import { ROUTES } from '@lib/routes';

export interface NavGroupTab {
  to: string;
  labelKey: string;
  permissions?: readonly PermissionKey[];
}

export interface NavGroup {
  /** The sidebar row's href: where the merged entry lands. */
  entry: string;
  /** The sidebar row's label, also the strip's accessible name. */
  labelKey: string;
  tabs: readonly NavGroupTab[];
}

export const NAV_GROUPS = {
  drivers: {
    entry: '/drivers',
    labelKey: 'nav.driversAndProfiles',
    tabs: [
      { to: '/drivers', labelKey: 'nav.drivers', permissions: [PERMISSIONS.DISPATCH_READ] },
      {
        to: '/driver-profiles',
        labelKey: 'nav.driverProfiles',
        permissions: [PERMISSIONS.DISPATCH_READ],
      },
    ],
  },
  audit: {
    entry: ROUTES.audit.root,
    labelKey: 'nav.audit',
    tabs: [
      { to: ROUTES.audit.operations, labelKey: 'auditWebhooks.tab.operations' },
      { to: ROUTES.audit.changes, labelKey: 'auditWebhooks.tab.changes' },
    ],
  },
  webhooks: {
    entry: ROUTES.webhooks.root,
    labelKey: 'nav.webhooks',
    tabs: [
      { to: ROUTES.webhooks.health, labelKey: 'auditWebhooks.tab.health' },
      { to: ROUTES.webhooks.events, labelKey: 'auditWebhooks.tab.events' },
      { to: ROUTES.webhooks.failures, labelKey: 'auditWebhooks.tab.failures' },
    ],
  },
  dishesCombos: {
    entry: '/catalog/dishes',
    labelKey: 'nav.kitchen.dishesCombos',
    tabs: [
      {
        to: '/catalog/dishes',
        labelKey: 'nav.catalog.dishes',
        permissions: [PERMISSIONS.CATALOG_READ],
      },
      {
        to: '/catalog/combos',
        labelKey: 'nav.catalog.combos',
        permissions: [PERMISSIONS.CATALOG_READ],
      },
    ],
  },
  ingredientsStock: {
    entry: '/catalog/ingredients',
    labelKey: 'nav.kitchen.ingredientsStock',
    tabs: [
      {
        to: '/catalog/ingredients',
        labelKey: 'nav.catalog.ingredients',
        permissions: [PERMISSIONS.CATALOG_READ],
      },
      {
        to: '/inventory/stock',
        labelKey: 'nav.inventory.stock',
        permissions: [PERMISSIONS.INVENTORY_READ, PERMISSIONS.CATALOG_READ],
      },
      {
        to: '/stocktake',
        labelKey: 'nav.stocktake.list',
        permissions: [PERMISSIONS.STOCKTAKE_READ],
      },
    ],
  },
  procurementSuppliers: {
    entry: '/procurement/purchase-orders',
    labelKey: 'nav.kitchen.procurementSuppliers',
    tabs: [
      {
        to: '/procurement/purchase-orders',
        labelKey: 'nav.procurement.purchaseOrders',
        permissions: [PERMISSIONS.PROCUREMENT_READ],
      },
      {
        to: '/procurement/suggestions',
        labelKey: 'nav.procurement.suggestions',
        permissions: [PERMISSIONS.PROCUREMENT_READ],
      },
      {
        to: '/catalog/suppliers',
        labelKey: 'nav.catalog.suppliers',
        permissions: [PERMISSIONS.CATALOG_READ],
      },
    ],
  },
} as const satisfies Record<string, NavGroup>;

export type NavGroupId = keyof typeof NAV_GROUPS;

const GROUP_BY_ENTRY = new Map<string, NavGroup>(
  Object.values(NAV_GROUPS).map((group) => [group.entry, group]),
);

/**
 * Every URL namespace a sidebar row with this href owns: its own href, plus
 * every tab route when the href is a merged entry.
 */
export function navNamespacesOf(href: string): readonly string[] {
  const group = GROUP_BY_ENTRY.get(href);
  if (group === undefined) return [href];
  return [...new Set([href, ...group.tabs.map((tab) => tab.to)])];
}
